import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, AlertTriangle, Clock } from 'lucide-react';

interface GaitMetrics {
  timestamp: string;
  walkingSpeed: number;
  stepLength: number;
  stepSymmetry: number;
  balanceScore: number;
  turnTime: number;
  stanceTime: number;
}

const GaitAnalysis: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const gaitData: GaitMetrics[] = [
    { timestamp: '6AM', walkingSpeed: 2.6, stepLength: 0.55, stepSymmetry: 92, balanceScore: 88, turnTime: 2.8, stanceTime: 0.9 },
    { timestamp: '9AM', walkingSpeed: 2.8, stepLength: 0.58, stepSymmetry: 94, balanceScore: 90, turnTime: 2.6, stanceTime: 0.85 },
    { timestamp: '12PM', walkingSpeed: 2.7, stepLength: 0.56, stepSymmetry: 93, balanceScore: 89, turnTime: 2.7, stanceTime: 0.88 },
    { timestamp: '3PM', walkingSpeed: 2.6, stepLength: 0.54, stepSymmetry: 91, balanceScore: 87, turnTime: 2.9, stanceTime: 0.92 },
    { timestamp: '6PM', walkingSpeed: 2.5, stepLength: 0.52, stepSymmetry: 90, balanceScore: 85, turnTime: 3.0, stanceTime: 0.95 },
    { timestamp: '9PM', walkingSpeed: 2.4, stepLength: 0.50, stepSymmetry: 89, balanceScore: 84, turnTime: 3.2, stanceTime: 0.98 }
  ];

  const currentMetrics = {
    averageSpeed: 2.6,
    averageStepLength: 0.54,
    symmetryScore: 91,
    balanceScore: 87,
    trends: {
      speed: '-3%',
      stepLength: 'stable',
      symmetry: '+2%',
      balance: '-1%'
    },
    riskLevel: 'MEDIUM'
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const getTrendColor = (trend: string) => {
    if (trend.startsWith('+')) return 'text-green-500';
    if (trend.startsWith('-')) return 'text-red-500';
    return 'text-gray-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Gait Analysis</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Walking Speed</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.averageSpeed} ft/s</p>
          <p className={`text-sm ${getTrendColor(currentMetrics.trends.speed)}`}>
            Trend: {currentMetrics.trends.speed}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Step Length</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.averageStepLength} m</p>
          <p className="text-sm text-gray-500">Status: {currentMetrics.trends.stepLength}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Symmetry Score</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.symmetryScore}%</p>
          <p className={`text-sm ${getTrendColor(currentMetrics.trends.symmetry)}`}>
            Trend: {currentMetrics.trends.symmetry}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Fall Risk Level</h3>
          </div>
          <p className={`text-3xl font-bold ${getRiskColor(currentMetrics.riskLevel)}`}>
            {currentMetrics.riskLevel}
          </p>
          <p className="text-sm text-gray-500">Based on gait analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Daily Walking Pattern</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={gaitData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="walkingSpeed" 
                name="Walking Speed (ft/s)"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="balanceScore" 
                name="Balance Score"
                stroke="#10b981"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Step Characteristics</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={gaitData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="stepLength" name="Step Length (m)" fill="#8b5cf6" />
              <Bar dataKey="stepSymmetry" name="Step Symmetry %" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Gait Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Afternoon Fatigue Pattern</h4>
              <p className="text-gray-600">Walking speed tends to decrease in the afternoon. Consider scheduling rest periods.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <TrendingUp className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Improved Step Symmetry</h4>
              <p className="text-gray-600">Step symmetry has improved by 2% over the past week, indicating better balance control.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Turn Time Analysis</h4>
              <p className="text-gray-600">Increased turn time in the evening suggests potential fatigue-related balance issues.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GaitAnalysis; 